import { EApplicantType } from '@if/core/types'
import { UpdatePrequalificationDto, useUpdatePrequalification } from '@src/api/credit-api'
import { useFeatureFlags } from '@src/api/FeatureSwitch-api'
import { Merchant, useMerchantById } from '@src/api/merchants-api'
import { Loader } from '@src/components'
import { getRecaptchaToken } from '@src/services/recaptcha'
import { scrollToTop } from '@src/services/utils'
import { ESessionStorageKeys } from '@src/types'
import { useCallback, useMemo, useState } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import { useTranslation } from 'react-i18next'
import { useNavigate, useParams } from 'react-router-dom'
import AddressForm from './components/AddressForm'
import PersonalInformationForm from './components/PersonalInformationForm'
import { Applicant, PersonalInformationInput, Prequalification } from './components/PrequalificationSchema'
import StepperFormFooter from './components/StepperFormFooter'
import { buildDefaultCoapplicant, buildDefaultPrequalification, useSessionStorage } from './submit-app-hooks'

const getDefaultPrequalInfo = (
  applicantType: EApplicantType,
  merchant: Merchant | null,
  urlMerchantId: string,
): Prequalification => {
  if (applicantType === EApplicantType.CoApplicant) {
    return buildDefaultCoapplicant()
  }
  return merchant
    ? buildDefaultPrequalification(merchant)
    : buildDefaultPrequalification({ id: urlMerchantId } as Merchant)
}

const UpdatePersonalInformationForm = () => {
  const { t } = useTranslation()
  const urlParams = useParams()
  const currentStep = urlParams.currentStep!
  const urlMerchantId = urlParams.merchantId!
  const { id, applicantType } = useParams<{ id: string; applicantType: EApplicantType }>()
  const [merchant, isFetchingMerchant] = useMerchantById(urlMerchantId)
  const [featureFlags, isFetchingFS] = useFeatureFlags()
  const useSelectForYearsOfResidenceAndEmployment = featureFlags?.useSelectForYearsOfResidenceAndEmployment ?? false

  const [prequalInfo, setPrequalInfo] = useSessionStorage<Prequalification>(
    applicantType === EApplicantType.CoApplicant
      ? ESessionStorageKeys.COAPPLICANT_INFO
      : ESessionStorageKeys.PREQUAL_INFO,
    getDefaultPrequalInfo(applicantType ?? EApplicantType.Applicant, merchant, urlMerchantId),
  )
  const [updatePrequal, isUpdating] = useUpdatePrequalification()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const navigate = useNavigate()
  const [disableNext, setDisableNext] = useState(false)

  const steps = useMemo(() => ['personal-information', 'address'], [])

  const cls = `form-section step-${currentStep}-active`

  const gotoStep = useCallback((stepPath: string) => {
    scrollToTop()
    navigate(`/${urlMerchantId}/creditapplication/${id}/editPersonalInformation/${applicantType}/${stepPath}`)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const nextStepAfter = useCallback(() => {
    const nextStepIdx = steps.indexOf(currentStep.toLowerCase()) + 1
    if (nextStepIdx < steps.length) {
      gotoStep(steps[nextStepIdx])
    }
  }, [currentStep, gotoStep, steps])

  const handlePrequalificationUpdated = (data: PersonalInformationInput) => {
    const { applicant, ...otherData } = data
    const updatedPrequalInfo = {
      ...prequalInfo,
      ...otherData,
      applicant: { ...prequalInfo.applicant, ...applicant },
    } as Prequalification
    setPrequalInfo(updatedPrequalInfo)
    nextStepAfter()
  }

  const handleApplicantUpdated = async (data: Partial<Applicant>) => {
    const update = { ...prequalInfo, applicant: { ...prequalInfo.applicant, ...data } }
    setPrequalInfo(update)

    const recaptchaToken = await getRecaptchaToken(executeRecaptcha, 'submit_personalInformation')
    const dto: UpdatePrequalificationDto = {
      creditApplicationId: urlParams.id!,
      personalInfoDto: {
        ...update,
      },
      recaptchaToken,
    }
    await updatePrequal(dto)
    navigate(`/creditapplication/${urlParams.id}`, { replace: true })
  }

  if (!urlMerchantId || isFetchingMerchant || isUpdating || !merchant || isFetchingFS)
    return <Loader title={t('common.withYouShortly')} description={t('common.pleaseWait')} />

  return (
    <main className={cls}>
      <h1 className="heading-primary">{t('identity.updateInfo')}</h1>

      {currentStep === 'personal-information' && (
        <PersonalInformationForm
          isCoapplicant={applicantType === EApplicantType.CoApplicant}
          prequalificationData={prequalInfo}
          onPrequalificationUpdated={handlePrequalificationUpdated}
          loanPurposeId={prequalInfo.loanPurposeId}
        />
      )}

      {currentStep === 'address' && (
        <AddressForm
          applicantData={prequalInfo.applicant}
          onApplicantUpdated={handleApplicantUpdated}
          applicantName={prequalInfo.applicant.firstName}
          setDisableNext={setDisableNext}
          useSelectForYearsOfResidenceAndEmployment={useSelectForYearsOfResidenceAndEmployment}
        />
      )}
      <StepperFormFooter
        currentStepIndex={steps.indexOf(currentStep) + 1}
        stepLength={steps.length}
        disableBack={false}
        disableNext={disableNext}
      />
    </main>
  )
}

export default UpdatePersonalInformationForm
